import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useParams,
	useLocation,
	useHistory
} from 'react-router-dom';
import {
	Header,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../services/utils';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../../../services/validation';

import useForm from '../../../../../hooks/useForm';

import {
	getContact,
	updateContact,
	createContact
} from '../../../../../api/nora/contacts';

import {
	useContactState,
	useContactAction
} from '../../../../../contexts/ContactsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import CredentialsTab from './CredentialsTab';
import EmailValidationInfoPopup from '../../../../Form/EmailValidationInfoPopup';
import InfoTab from './InfoTab';
import PhoneValidationInfoPopup from '../../../../Form/PhoneValidationInfoPopup';

const ContactDetails = () => {
	const history = useHistory();
	const { id } = useParams();
	const { state: navigationState } = useLocation();

	const {
		contact,
		initialized
	} = useContactState();
	const {
		setContact,
		clearContact,
		setTags
	} = useContactAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( 0 );
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ contactSavedAlert, setContactSavedAlert ] = useState( false );
	const [ pendingIndex, setPendingIndex ] = useState();
	// const [ createdTagss, setCreatedTags ] = useState( [] );

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		resetFormData,
		setFormData
	} = useForm();

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: setInfobar( <span>Operations &nbsp;&#9656;&nbsp; <Link to="/operations/contacts">Contacts</Link> &nbsp;&#9656;&nbsp; { contact?.firstName && contact?.lastName ? `${ contact.firstName } ${ contact.lastName }` : 'New Contact' }</span> );
	}, [
		contact,
		navigationState,
		setInfobar
	] );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait...' );
		} else {
			setTags();

			if ( id > 0 ) { // not a new contact
				( async () => {
					setInfobarLoader( 'Retrieving Contact Info...' );

					try {
						const response = await getContact( id );

						setContact( response );
					} catch ( error ) {
						setApiErrorAlert( error );
					}
				} )();
			}
		}

		return () => {
			clearContact();
		};
	}, [
		clearContact,
		id,
		initialized,
		setApiErrorAlert,
		setContact,
		setInfobarLoader,
		setTags
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( contact ) { // if it is not a new contact
			const tags = contact.labels // labels on backend
				? contact.labels.map( ( { id } ) => id ) // labels on backend
				: [];

			setFormData( {
				...contact,
				type: contact.type.id,
				organization: contact.organization.id,
				phone: contact.phone.number,
				ext: contact.phone.extension,
				SMSEnabled: contact.phone.isSMSEnabled,
				fax: contact.fax.number,
				tags,
				...navigationState && navigationState.initialData
			} );
		} else if ( id === '0' ) {
			setFormData( {
				id: 0,
				tags: [],
				...navigationState && navigationState.initialData
			} );
		}
	}, [
		contact,
		id,
		navigationState,
		setFormData
	] );

	useEffect( () => {
		if ( initialized && Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		initialized,
		setInfobarLoader
	] );

	const errors = useMemo( () => ( {
		type: [
			{
				error: !formData.type,
				message: 'Contact Type is a required field.',
				fieldLabel: 'Contact Type'
			}
		],
		firstName: [
			{
				error: !formData.firstName,
				message: 'First Name is a required field.',
				fieldLabel: 'First Name'
			}
		],
		lastName: [
			{
				error: !formData.lastName,
				message: 'Last Name is a required field.',
				fieldLabel: 'Last Name'
			}
		],
		organization: [
			{
				error: !formData.organization,
				message: 'Organization is a required field.',
				fieldLabel: 'Organization'
			}
		],
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		fax: [
			{
				error: Boolean( formData.fax ) && !validatePhone( formData.fax ),
				message: <>Please enter a valid U.S. Phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Fax'
			}
		]
	} ), [
		formData.email,
		formData.fax,
		formData.firstName,
		formData.lastName,
		formData.organization,
		formData.phone,
		formData.type,
		formData.zip
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const handleSave = useCallback( async cb => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			const labels = formData.tags.length // labels on backend
				? formData.tags.reduce( ( acc, tag ) => {
					return [
						...acc,
						{ id: tag }
					];
				}, [] )
				: [];

			let {
				type,
				organization,
				organizationName,
				phone,
				ext,
				SMSEnabled,
				fax,
				...rest
			} = formData;

			const data = {
				...rest,
				type: { id: type },
				organization: {
					id: parseInt( organization ),
					name: organizationName
				},
				phone: {
					number: phone,
					extension: ext,
					isSMSEnabled: SMSEnabled
				},
				fax: { number: fax },
				labels // labels on backend
			};

			try {
				setNavLock( false );

				let newId = 0;

				if ( formData.id === 0 ) {
					const response = await createContact( data );

					newId = response.id;

					setContact( response );
				} else {
					const response = await updateContact( data );

					setContact( response );
				}

				setTags();
				// setCreatedTags( [] );
				setContactSavedAlert( true );

				cb?.();

				if ( newId ) {
					history.push( `/operations/contacts/${ newId }` );
				}
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		forceValidation,
		formData,
		history,
		setApiErrorAlert,
		setContact,
		setTags,
		setNavLock,
		validate
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderInfoTab = useCallback( () => (
		<InfoTab
			// createdTagss={ createdTagss }
			errors={ errors }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			locks={ navigationState?.lockedData || [] }
			resetFormData={ resetFormData }
			// setCreatedTags={ setCreatedTags }
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		// createdTagss,
		errors,
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		navigationState,
		resetFormData,
		updateField,
		validate
	] );

	const renderCredentialsTab = useCallback(
		() => <CredentialsTab />
		, []
	);

	const renderPanes = useCallback( () => {
		let panes = [
			{
				menuItem: 'Info',
				render: renderInfoTab
			}
		];

		if ( formData.id > 0 && ( formData.type === 2 || formData.type === 3 ) ) { // staff or agency
			panes = [
				...panes,
				{
					menuItem: 'Credentials',
					render: renderCredentialsTab
				}
			];
		}

		return panes;
	}, [
		formData.type,
		formData.id,
		renderCredentialsTab,
		renderInfoTab
	] );

	const handleUnsavedProgress = useCallback( async () => {
		await handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeContactSavedAlert = useCallback( () => {
		setContactSavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		if ( !validate() ) {
			if ( forceValidation ) {
				setForceValidation( false );
			}

			if ( formDataHasChanged ) {
				setPendingIndex( activeIndex );
				setUnsavedProgressAlert( true );
			} else {
				setActiveIndex( activeIndex );
			}
		} else if ( !forceValidation ) {
			setForceValidation( true );
		}
	}, [
		forceValidation,
		formDataHasChanged,
		validate
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'contactDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ renderPanes() }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and continue"
				name="contact-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				closeText="Go back"
				type="confirm"
			/>
			<Alert
				closeModal={ closeContactSavedAlert }
				message={
					<Header
						as="h1"
						color="pink"
					>
					Saved!
					</Header>
				}
				name="contact-saved-notification"
				open={ contactSavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( ContactDetails );
